import { GridDataSource, GridGetRowsParams, GridGetRowsResponse } from "@mui/x-data-grid-pro";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { EditableGridStateColDef, OnFilterOnSortCallBackProps } from "../types";
import { buildOrderFromModel } from "../utils/buildSortFromFilters";
import { buildWhereFromFilters } from "../utils/buildWhereFromFilters";
import { GridApiPro } from "@mui/x-data-grid-pro";

/**
REQUIREMENTS

- when data is fetched for the first time and scrollToRowId = undefined: set the first row as selected (consider that on scrolling the data is refetched and we should not keep re-selecting the first row)
- when data is fetched and scrollToRowId = defined: set selected row id to scrollToRowId, and scroll it into view, centering it in the table. Then reset scrollToRowId to undefined

Note: since infinite scrolling only fetches per batch, the scrollToRowId may not be present in the current batch. Current solution is to fetch all the available data in this use case

- data should be refetched when shouldRefreshList = true. Once refetched, resetShouldRefreshList should be called to reset the flag. When refetching, filters and quick filters should be cleared.
- on sorting and filtering the data should be refetched with them applied

Note: onFilterModelChange and onSortModelChange should take care of refetching, but they ignore the apiRef filter & sort model, so this is handled by listening to variables change in dataSource getRows & 
extracting the filter & sort models from the params

- the total rows count should be tracked and updated on every fetch, and exposed to the parent via setTotalRowsCount

 */

/**
 * Infinite-scroll data source hook for MUI DataGrid Pro.
 * - Converts grid filter/sort state to GraphQL where/order arguments.
 * - Fetches slices based on scroll position (skip/take) while tracking total rows.
 * - Maintains selection and optional scroll-to-row behavior.
 * - Uses setFirstRowAsSelected to pick the true first row of the full filtered/sorted dataset.
 *   Rationale: selecting the "first row" from the current dataSource page would only target
 *   the first row of that batch, not the top row of the entire table. The dedicated one-row
 *   fetch avoids selecting a page-local row and ensures consistent initial selection.
 */
/**
 * Hook for implementing infinite scroll with MUI DataGrid's Data Source layer
 */
export function useDataSource<E extends keyof EntityMappings>({
  apiRef,
  query,
  variables = {},
  enabled = true,
  scrollToRowId,
  setSelectedRow,
  shouldRefreshList,
  resetShouldRefreshList,
  resetScrollToRowId,
  setTotalRowsCount,
}: OnFilterOnSortCallBackProps<E> & {
  /**
   * Toggle the entire hook. When false, no data source is returned and no effects run.
   */
  enabled?: boolean;
  /**
   * When set, fetches without pagination limits, scrolls to this row, selects it, then clears via resetScrollToRowId.
   */
  scrollToRowId?: string;
  /**
   * Setter used by the hook to update the currently selected row (first-row selection or scroll target).
   */
  setSelectedRow?: (rowId: string | undefined) => void;
  /**
   * When true, the hook clears filters/quick filters, triggers a refetch, and re-selects the first row.
   */
  shouldRefreshList?: boolean;
  /**
   * Resets the refresh flag after the refresh cycle completes.
   */
  resetShouldRefreshList?: () => void;
  /**
   * Clears scrollToRowId after the scroll-to-selected behavior finishes.
   */
  resetScrollToRowId?: () => void;
  setTotalRowsCount?: (count: number) => void;
}): {
  dataSource: GridDataSource | undefined;
  totalRowsCount: number | undefined;
} {
  const varsStr = JSON.stringify(variables);
  // Track whether the initial first-row selection has already been performed
  // for the current query context.
  const hasSetInitialSelectionRef = useRef(false);
  // Keep scrollToRowId accessible inside the memoized dataSource without
  // adding it to the memo deps — adding it would recreate dataSource on reset
  // (when resetScrollToRowId clears it), which clears the grid's row cache and
  // resets scroll position. Using a ref gives getRows the latest value each
  // call without triggering a memo rebuild.
  const scrollToRowIdRef = useRef(scrollToRowId);
  useEffect(() => {
    scrollToRowIdRef.current = scrollToRowId;
  }, [scrollToRowId]);
  // Avoid calling setTotalRowsCount (and triggering parent re-renders) when
  // the total count hasn't actually changed between scroll fetches.
  const lastReportedRowCountRef = useRef<number | undefined>(undefined);
  // Tracks the effective query criteria (where/order + static variables).
  // When this changes, a new dataset is being shown and first-row selection
  // should run again.
  const selectionCriteriaRef = useRef("");

  /**
   * Waits until `apiRef.current` is defined, polling on each animation frame.
   * Resolves with the api or `null` if it never appears within `maxAttempts`.
   */
  const waitForApiRef = useCallback(
    (maxAttempts = 60): Promise<GridApiPro | null> =>
      new Promise((resolve) => {
        let attempts = 0;
        const tick = () => {
          if (apiRef?.current) return resolve(apiRef.current);
          if (++attempts >= maxAttempts) return resolve(null);
          requestAnimationFrame(tick);
        };
        tick();
      }),
    [apiRef]
  );

  /**
   * Runs after every successful getRows. Selects the appropriate row and,
   * when scrollToRowId is set, scrolls it into view. Safe against apiRef
   * being undefined at call time � it waits for it.
   */
  const getSelectedRow = useCallback(
    async (rows: { id: string }[], start = 0, selectionCriteriaChanged = false) => {
      if (!enabled) {
        setSelectedRow?.(undefined);
        return;
      }

      // Preserve selection only for empty pagination fetches in the same result set.
      if (!rows?.length && start > 0 && !selectionCriteriaChanged) return;

      if (!rows?.length) {
        // Variables/filters changed and no rows match: clear selection.
        // Reset the guard so first-row selection can run again once rows reappear.
        hasSetInitialSelectionRef.current = false;
        setSelectedRow?.(undefined);
        return;
      }

      if (hasSetInitialSelectionRef.current) return;

      // No scroll target � just select the first row of the dataset.
      if (!scrollToRowId) {
        hasSetInitialSelectionRef.current = true;
        setSelectedRow?.(rows[0]?.id);
        return;
      }

      hasSetInitialSelectionRef.current = true;

      const api = await waitForApiRef();
      if (!api) {
        // Re-arm if grid API isn't ready yet so a subsequent fetch can retry selection.
        hasSetInitialSelectionRef.current = false;
        return;
      }

      const rowIndex = rows.findIndex((row) => row.id === scrollToRowId);
      if (rowIndex < 0) {
        // Target not in this batch. Do NOT fall back to rows[0] � that would
        // silently override the user's intended selection on subsequent
        // infinite-scroll fetches. Leave the guard un-set so a later batch
        // containing the target can still trigger the scroll.
        hasSetInitialSelectionRef.current = false;
        return;
      }

      setSelectedRow?.(scrollToRowId);

      // Wait until the grid has committed the rows we just returned, then
      // scroll. The `rowsSet` event fires after rows are inserted into state.
      const performScroll = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const rowHeight = api.unstable_getRowHeight?.(scrollToRowId);
            // Viewport height of the scrollable area (excludes header/footer)
            const viewportHeight = api.getRootDimensions?.().viewportInnerSize?.height ?? 0;
            // Top pixel offset of the target row within the scroller.
            // Sum of heights of all rows above the target.
            const rowTop = rows
              .slice(0, rowIndex)
              .reduce((acc, r) => acc + (api.unstable_getRowHeight?.(r.id) ?? rowHeight), 0);
            // Place the row's vertical center at the viewport's vertical center.
            const top = Math.max(0, rowTop - viewportHeight / 2 + rowHeight / 2);
            api.scrollToIndexes?.({ rowIndex, colIndex: 0 });
            // scroll with padding to item
            api.scroll?.({ top });
            resetScrollToRowId?.();
          });
        });
      };

      const unsub = api.subscribeEvent?.("rowsSet", () => {
        unsub?.();
        performScroll();
      });

      // Safety fallback in case `rowsSet` doesn't fire (already committed).
      setTimeout(() => {
        unsub?.();
        performScroll();
      }, 300);
    },
    [apiRef, enabled, scrollToRowId, setSelectedRow, resetScrollToRowId, waitForApiRef]
  );

  // Keep getSelectedRow accessible from inside the memoized dataSource without
  // putting it in the memo deps � otherwise scrollToRowId changes would rebuild
  // dataSource and (combined with the guard reset) cause later batches to
  // re-run first-row selection and override the user's chosen row.
  const getSelectedRowRef = useRef(getSelectedRow);
  useEffect(() => {
    getSelectedRowRef.current = getSelectedRow;
  }, [getSelectedRow]);

  // Reset the initial-selection guard and the cached row count only when the
  // query identity actually changes (variables/enabled), not on every memo recompute.
  useEffect(() => {
    hasSetInitialSelectionRef.current = false;
    lastReportedRowCountRef.current = undefined;
  }, [varsStr, enabled]);

  const dataSource = useMemo<GridDataSource>(() => {
    return {
      getRows: async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
        if (!enabled) return { rows: [] };
        try {
          const start = typeof params.start === "number" ? params.start : 0;
          const pageSize = variables?.take ?? 15;
          const take = Math.max(params.end - start + 1, pageSize);

          // Build filter and sort from the values supplied by the data source
          // for this fetch (rather than reading them off apiRef state). Column
          // metadata still comes from apiRef since it isn't part of `params`.
          const columns = (apiRef?.current?.getAllColumns?.() ?? []) as EditableGridStateColDef[];

          const where = buildWhereFromFilters({
            variables,
            columns,
            filterModel: params.filterModel ?? { items: [] },
          });

          const order = buildOrderFromModel({ sortModel: params.sortModel ?? [], variables, columns });

          const selectionCriteria = JSON.stringify({ varsStr, where, order, hasScrollTarget: !!scrollToRowId });
          const selectionCriteriaChanged = selectionCriteriaRef.current !== selectionCriteria;
          if (selectionCriteriaChanged) {
            hasSetInitialSelectionRef.current = false;
            selectionCriteriaRef.current = selectionCriteria;
          }

          // Read the latest scrollToRowId from the ref (not the stale closure
          // value) so that once resetScrollToRowId() clears it, subsequent
          // scroll fetches use normal skip/take pagination instead of
          // re-fetching 1000 rows from the top every time.
          const currentScrollToRowId = scrollToRowIdRef.current;
          const result = await query({
            variables: {
              take: currentScrollToRowId ? 1000 : take, // for now take all until we have a better solution for fetching the page containing scrollToRowId
              skip: currentScrollToRowId ? 0 : start,
              order,
              where,
            },
          });

          const entityKey = Object.keys(result?.data || {}).find((key) => key !== "__typename");
          const entityData = entityKey ? result?.data?.[entityKey] : null;
          const rows = entityData?.items ?? [];
          const rowCount = entityData?.totalCount;
          const hasNextPage = start + rows.length < rowCount;

          if (rowCount !== lastReportedRowCountRef.current) {
            lastReportedRowCountRef.current = rowCount;
            setTotalRowsCount?.(rowCount);
          }
          getSelectedRowRef.current(rows, start, selectionCriteriaChanged);

          return {
            rows,
            pageInfo: { hasNextPage },
          };
        } catch (error) {
          console.error("Error fetching rows for infinite scroll:", error);
          return { rows: [], rowCount: 0 };
        }
      },
    };
  }, [varsStr, enabled]);

  // Refetch on demand when shouldRefreshList toggles to true.
  // Clears filters/quick filters first (per requirements), then clears the
  // data source cache and asks the grid to refetch the first page. The grid's
  // own lazy loader takes care of resetting stale state and skeleton rows.
  // Resets the flag once the refetch has been triggered.
  useEffect(() => {
    if (!shouldRefreshList || !enabled) return;

    let cancelled = false;
    setSelectedRow?.(undefined); // clear selection on refresh
    (async () => {
      const api = await waitForApiRef();

      if (cancelled || !api) return;

      // Clear filters and quick filters before refetching.
      api.setFilterModel?.({ items: [], quickFilterValues: [] });

      // Invalidate cache and trigger a fresh fetch through the data source layer.
      api.dataSource?.cache?.clear?.();
      hasSetInitialSelectionRef.current = false;
      api.dataSource?.fetchRows?.();

      resetShouldRefreshList?.();
    })();
    return () => {
      cancelled = true;
    };
  }, [shouldRefreshList, enabled, resetShouldRefreshList, waitForApiRef]);

  return {
    dataSource,
    totalRowsCount: 0,
  };
}
